import React from "react";
import { Modal, Tag, Card, Empty, Button, Tooltip, message } from "antd";
import { EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import Loader from "components/common/Loader";
import { downloadS3File } from "utils/downloadFile";

const getFileExtension = (url = "") => {
	if (!url) return "";
	const base = url.split("?")[0] || "";
	const match = base.match(/\.[0-9a-z]+$/i);
	return match ? match[0] : "";
};

const buildDispatchAckName = (jobcardSerial, detailId) =>
	`Jobcard_${jobcardSerial || "NA"}_DispatchAck_${detailId}`;

export const DispatchSummaryModal = ({
	visible,
	jobcardId,
	jobcardSerialNumber,
	loading,
	details = [],
	onClose,
	onPreview,
	formatters = {},
}) => {
	const {
		formatDateTimeDisplay = (value) => value || "—",
		formatDateDisplay = (value) => value || "—",
		formatTimeDisplay = (value) => value || "—",
	} = formatters;

	return (
		<Modal
			title={`Jobcard #${jobcardSerialNumber || jobcardId || ""}`}
			open={visible}
			onCancel={onClose}
			footer={[
				<Button key="close" onClick={onClose}>
					Close
				</Button>,
			]}
			width={720}
			centered
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					flexWrap: "wrap",
					rowGap: 8,
					marginBottom: 16,
				}}
			>
				<Tag color="geekblue" style={{ fontSize: 14, padding: "4px 12px" }}>
					Jobcard #{jobcardSerialNumber || jobcardId || "—"}
				</Tag>
				<span style={{ fontSize: 16, fontWeight: 600, color: "#0f172a" }}>
					Dispatch Details Summary
				</span>
			</div>
			{loading ? (
				<Loader />
			) : details.length ? (
				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					{details.map((detail) => (
						<Card
							key={detail.id}
							hoverable
							style={{
								borderRadius: 12,
								borderColor: "#e2e8f0",
								boxShadow: "0 4px 12px rgba(15,23,42,0.06)",
							}}
							bodyStyle={{ display: "flex", flexDirection: "column", gap: 10 }}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 8,
									flexWrap: "wrap",
								}}
							>
								<div>
									<div style={{ fontWeight: 600, color: "#0f172a" }}>Recorded</div>
									<div style={{ fontSize: 12, color: "#475569" }}>
										{formatDateTimeDisplay(detail.created_at)}
									</div>
								</div>
								{detail.created_by && <Tag color="blue">{detail.created_by}</Tag>}
							</div>

							<div
								style={{
									display: "grid",
									gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
									gap: 12,
								}}
							>
								<div>
									<div style={{ fontWeight: 600 }}>Delivery Date</div>
									<div style={{ color: "#334155" }}>
										{formatDateDisplay(detail.delivery_date)}
									</div>
								</div>
								<div>
									<div style={{ fontWeight: 600 }}>Time</div>
									<div style={{ color: "#334155" }}>
										{formatTimeDisplay(detail.delivery_time)}
									</div>
								</div>
								<div>
									<div style={{ fontWeight: 600 }}>Delivery Note</div>
									<div style={{ color: "#334155" }}>{detail.delivery_note || "—"}</div>
								</div>
							</div>

							{Array.isArray(detail.jobcards) && detail.jobcards.length > 0 ? (
								<div>
									<div style={{ fontWeight: 600, marginBottom: 4 }}>Jobcards</div>
									<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
										{detail.jobcards.map((jobcard) => (
											<Tag key={`${detail.id}-${jobcard.id}`} color="geekblue">
												#{jobcard.serial_number || jobcard.id}
												{jobcard.client ? ` · ${jobcard.client}` : ""}
											</Tag>
										))}
									</div>
								</div>
							) : null}
                            <h5>Delivery Acknowledgement</h5>

							{detail.delivery_acknowledgement_url ? (
								<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<Tooltip title="View acknowledgement">
										<Button
											size="small"
											icon={<EyeOutlined />}
											onClick={() =>
												onPreview?.({
													url: detail.delivery_acknowledgement_url,
													name: buildDispatchAckName(jobcardSerialNumber || jobcardId, detail.id),
												})
											}
										/>
									</Tooltip>
									<Tooltip title="Download acknowledgement">
										<Button
											size="small"
											icon={<DownloadOutlined />}
											onClick={async () => {
												const url = detail.delivery_acknowledgement_url;
												if (!url) return;
												try {
													await downloadS3File(
														url,
														`${buildDispatchAckName(jobcardSerialNumber || jobcardId, detail.id)}${getFileExtension(
															url
														)}`,
														{ neverOpenNewWindow: true }
													);
												} catch (error) {
													message.error("Unable to download acknowledgement file.");
												}
											}}
										/>
									</Tooltip>
								</div>
							) : (
								<Tag color="default">No acknowledgement uploaded</Tag>
							)}
						</Card>
					))}
				</div>
			) : (
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No dispatch records found for this jobcard." />
			)}
		</Modal>
	);
};

export const DispatchPreviewModal = ({ preview, onClose }) => {
	const url = preview?.url;
	const lower = String(url || "").toLowerCase();
	const isPdf = url ? lower.endsWith(".pdf") : false;
	const isImage = url ? /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower) : false;

	return (
		<Modal
			open={!!preview}
			title={preview ? preview.name : "Dispatch Acknowledgement"}
			onCancel={onClose}
			footer={[
				<Button key="close" onClick={onClose}>
					Close
				</Button>,
				url
					? (
							<Button
								key="download"
								type="primary"
								icon={<DownloadOutlined />}
								onClick={() =>
									downloadS3File(url, `${preview?.name}${getFileExtension(url)}`, {
										neverOpenNewWindow: true,
									}).catch(() => message.error("Unable to download acknowledgement file."))
								}
							>
								Download
							</Button>
					  )
					: null,
			].filter(Boolean)}
			width={700}
		>
			{preview ? (
				url ? (
					isPdf ? (
						<iframe
							src={`${url}#toolbar=1&navpanes=0&scrollbar=1`}
							title="Dispatch Acknowledgement"
							style={{
								width: "100%",
								height: "70vh",
								border: "none",
								borderRadius: 8,
								background: "#fff",
							}}
						/>
					) : isImage ? (
						<img
							src={url}
							alt={preview.name || "Dispatch Acknowledgement"}
							style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: 8 }}
						/>
					) : (
						<div style={{ textAlign: "center", padding: "40px 0" }}>
							<span role="img" aria-label="file" style={{ fontSize: 64, lineHeight: 1 }}>
								🗂️
							</span>
							<div style={{ marginTop: 16 }}>Preview not available for this file type.</div>
						</div>
					)
				) : (
					<div>No preview available.</div>
				)
			) : null}
		</Modal>
	);
};

